import axios, {
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
} from "axios";
import { logger } from "./logger";
import axiosRetry from "axios-retry";
import { useAuthStore } from "../store/useAuthStore";
import { b2cLoginRequest, loginRequest } from "../auth/authConfig";
import { msalInstance } from "../auth/msalInstance";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { isValidationErrorResponse } from "../utils/handleError";

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

// Error Handling
// -------------------------------------------------------------------------------
const handleResponseError = (error: AxiosError): Promise<never> => {
  const { response, request, config } = error;

  if (response) {
    if (response.status === 400 && isValidationErrorResponse(response.data)) {
      return Promise.reject(error);
    }

    if (response.status >= 500) {
      logger.error("Axios", `HTTP ${response.status} Error`, {
        url: config?.url,
        method: config?.method?.toUpperCase(),
        status: response.status,
        data: response.data,
        error: error.message,
      });
    } else {
      logger.info("Axios", `HTTP ${response.status} Response`, {
        url: config?.url,
        method: config?.method?.toUpperCase(),
        status: response.status,
        data: response.data,
        error: error.message,
      });
    }
  } else if (request) {
    logger.info("Axios", "Network Error", {
      url: config?.url,
      error: error.message,
    });
  } else {
    logger.error("Axios", "Request Setup Error", {
      url: config?.url,
      error: error.message,
    });
  }

  return Promise.reject(error);
};

// Configuration
// -------------------------------------------------------------------------------
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
const apiTimeout = import.meta.env.VITE_API_TIMEOUT || 10000;

const apiParams = {
  baseURL: apiBaseUrl,
  timeout: apiTimeout,
  headers: { "Content-Type": "application/json" },
};

// API Client Setup
// -------------------------------------------------------------------------------
const apiClient = axios.create(apiParams);

// Automatic Retry Logic
axiosRetry(apiClient, {
  retries: 3, // Maximum retry attempts
  retryDelay: (retryCount) => retryCount * 1000, // Exponential backoff (1s, 2s, 3s)
  retryCondition: (error) => {
    const { response } = error;
    if (!axios.isCancel(error)) {
      if (!response) return true;
      return [502, 503, 504].includes(response.status);
    }
    return false;
  },
});

// Request Interceptor
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    let accessToken = useAuthStore.getState().token;
    const account = useAuthStore.getState().activeAccount;

    const isB2C = useAuthStore.getState().isB2C;

    const scopes = isB2C ? b2cLoginRequest.scopes : loginRequest.scopes;

    if (!accessToken && account) {
      try {
        if (isB2C) {
          logger.info(
            "Auth",
            "B2C access token missing/expired - interaction required"
          );
          throw new InteractionRequiredAuthError();
        }

        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes,
          account,
        });

        accessToken = tokenResponse.accessToken;
        useAuthStore.getState().setToken(accessToken);
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          logger.info(
            "Auth",
            "Silent refresh failed - user interaction required",
            error
          );
        } else {
          logger.error("Auth", "Silent refresh failed", error);
        }

        throw error;
      }
    }

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response Interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const store = useAuthStore.getState();
    const isB2C = store.isB2C;

    // External user (b2c)
    if (isB2C && error.response?.status === 401) {
      logger.info("Auth", "B2C 401 — clearing auth state");

      store.setToken(null);
      store.setActiveAccount(null);
      store.setLoginError("");
      store.setAuthType(false);

      return Promise.reject(error);
    }

    // internal user (msal)
    if (!isB2C && error.response?.status === 401) {
      const config = error.config as CustomAxiosRequestConfig;

      if (config._retry) {
        logger.info("Auth", "401 retry already attempted — clearing session");
        store.setToken(null);
        store.setActiveAccount(null);
        return Promise.reject(error);
      }

      config._retry = true;

      const account = store.activeAccount;
      if (!account) {
        store.setToken(null);
        return Promise.reject(error);
      }

      try {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes: loginRequest.scopes,
          account,
        });

        const newAccessToken = tokenResponse.accessToken;
        store.setToken(newAccessToken);

        return apiClient.request({
          ...config,
          headers: {
            ...config.headers,
            Authorization: `Bearer ${newAccessToken}`,
          },
        });
      } catch (refreshError) {
        logger.info(
          "Auth",
          "Internal token refresh failed — clearing session",
          refreshError
        );

        store.setToken(null);
        store.setActiveAccount(null);

        return Promise.reject(refreshError);
      }
    }

    return handleResponseError(error);
  }
);

// Type-safe Response Handler
// -------------------------------------------------------------------------------
const responseBody = <T>(response: AxiosResponse<T>): T => response.data;

// Type-safe Request Methods
// -------------------------------------------------------------------------------
const requests = {
  get: <T>(
    url: string,
    signal?: AbortSignal,
    params?: unknown,
    config?: AxiosRequestConfig
  ) => apiClient.get<T>(url, { params, signal, ...config }).then(responseBody),
  post: <T>(
    url: string,
    body: unknown,
    config?: AxiosRequestConfig,
    signal?: AbortSignal
  ) => apiClient.post<T>(url, body, { signal, ...config }).then(responseBody),
  put: <T>(url: string, body?: unknown) =>
    apiClient.put<T>(url, body).then(responseBody),
  patch: <T>(url: string, body: unknown) =>
    apiClient.patch<T>(url, body).then(responseBody),
  delete: <T>(url: string) => apiClient.delete<T>(url).then(responseBody),
};

export { apiClient, requests };
